import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, Flame, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to generate slug
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

import { getTimeAgo } from '../lib/dateUtils';

/**
 * Trending Articles Section
 * Displays articles with the most engagement in the last 24-48 hours
 * Desktop: Auto-scroll with pause on hover, drag support, prev/next arrows
 */
const TrendingSection = ({ isKinyarwanda = false, onArticleClick }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    fetchTrendingArticles();
  }, []);

  const fetchTrendingArticles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/articles/trending?limit=10`);
      setArticles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch trending articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Create duplicated articles for infinite loop effect
  const displayArticles = articles.length > 0 ? [...articles, ...articles] : [];
  
  // Auto-scroll functionality with infinite loop
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || articles.length === 0) return;

    const scroll = () => {
      if (isHovered || isDragging) return;
      
      // Calculate the width of one set of articles (half of total content)
      const halfScrollWidth = container.scrollWidth / 2;
      const currentScroll = container.scrollLeft;
      
      // When we've scrolled past the first set, instantly reset to start (no animation)
      // This creates the illusion of infinite scrolling
      if (currentScroll >= halfScrollWidth) {
        container.scrollLeft = 0;
      } else {
        // Smooth continuous scroll
        container.scrollBy({ left: 2, behavior: 'auto' });
      }
    };

    // Start auto-scroll interval (smooth continuous scroll)
    autoScrollRef.current = setInterval(scroll, 30);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [articles, isHovered, isDragging]);

  // Handle mouse down for drag
  const handleMouseDown = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    container.style.cursor = 'grabbing';
  }, []);

  // Handle mouse move for drag
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 2;
    container.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  // Handle mouse up/leave
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = 'grab';
    }
  }, []);

  // Handle scroll buttons - quick scroll like mouse drag
  const scrollByAmount = (direction, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const cardWidth = 280; // Card width + gap
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    
    // Quick scroll (no smooth behavior - instant like mouse drag)
    container.scrollLeft = container.scrollLeft + scrollAmount;
  };

  if (loading) {
    return (
      <section data-testid="trending-section-loading" className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="flex gap-4 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-64">
                <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
                <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section 
      data-testid="trending-section" 
      className="bg-gradient-to-r from-red-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 border-b-4 border-[#D90429] dark:border-gray-800"
      onMouseEnter={() => setShowArrows(true)}
      onMouseLeave={() => setShowArrows(false)}
    >
      <div className="max-w-6xl mx-auto px-4 py-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-[#D90429] rounded-lg">
            <Flame className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wide" style={{ fontFamily: "'Public Sans', sans-serif" }}>
            {isKinyarwanda ? 'Inkuru Zishyushye' : 'Trending Now'}
          </h2>
          <span className="text-xs text-[#047857] dark:text-emerald-400 ml-1 font-medium" style={{ fontFamily: "'Public Sans', sans-serif" }}>
            • {isKinyarwanda ? 'Mu masaha 24' : 'Last 24 hours'}
          </span>
        </div>

        {/* Scroll Container with Arrows */}
        <div className="relative group">
          {/* Left Arrow - Show on hover */}
          <button
            onClick={(e) => scrollByAmount('left', e)}
            onMouseDown={(e) => e.stopPropagation()}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white dark:bg-gray-800 shadow-lg rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 ${
              showArrows ? 'opacity-100 -translate-x-1/2' : 'opacity-0 -translate-x-full pointer-events-none'
            }`}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Right Arrow - Show on hover */}
          <button
            onClick={(e) => scrollByAmount('right', e)}
            onMouseDown={(e) => e.stopPropagation()}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white dark:bg-gray-800 shadow-lg rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 ${
              showArrows ? 'opacity-100 translate-x-1/2' : 'opacity-0 translate-x-full pointer-events-none'
            }`}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Horizontal Scroll Container with drag support */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide cursor-grab select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => { setIsHovered(false); handleMouseUp(); }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            {displayArticles.map((article, index) => {
              const title = isKinyarwanda && article.title_rw 
                ? article.title_rw 
                : (article.title_en || article.title);
              
              return (
                <Link
                  key={`${article.id}-${index}`}
                  to={`/news/${generateSlug(article.title)}`}
                  data-testid={`trending-article-${index}`}
                  className="flex-shrink-0 w-56 sm:w-64 group/card"
                  onClick={(e) => {
                    if (isDragging) {
                      e.preventDefault();
                      return;
                    }
                    const isExternal = article.is_fetched || (article.link && !article.is_user_created);
                    if (isExternal && article.link) {
                      e.preventDefault();
                      window.open(article.link, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    if (onArticleClick) {
                      e.preventDefault();
                      onArticleClick(article);
                    }
                  }}
                  draggable={false}
                >
                  {/* Image */}
                  <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    {article.image_url ? (
                      <img
                        src={article.image_url}
                        alt=""
                        className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                        loading="lazy"
                        draggable={false}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-red-100 dark:from-gray-800 dark:to-gray-700">
                        <TrendingUp className="h-8 w-8 text-orange-300 dark:text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="mt-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white leading-snug line-clamp-2 group-hover/card:text-orange-600 dark:group-hover/card:text-orange-400 transition-colors">
                      {title}
                    </h3>
                    
                    {/* Time only */}
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3" />
                      {getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default TrendingSection;
